import { produce } from 'immer';
import _ from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { cardTypeDescriptionMapping } from '../..';
import {
  formatPrice,
  updateQueryStringParameter,
} from '../../../../../../../common/global/helpers';
import Button from '../../../../../../../common/widgets/Button';
import Dropdown from '../../../../../../../common/widgets/Dropdown';
import Icons from '../../../../../../../common/widgets/Icons';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import PopoverDialog from '../../../../../../../common/widgets/PopoverDialog';
import { config } from '../../../../../config';
import { CardFormFactor, CardType } from '../../../../../enums';
import { rewriteImageUrl } from '../../../../../helpers';
import {
  CardProductModel,
  ComputedCardProductPriceModel,
} from '../../../../../models/Cards';
import { Dictionary } from '../../../../../models/Categories';
import { ErrorState } from '../../../../../models/Errors';
import { DependentModel } from '../../../../../models/Options';
import { resources } from '../../../../../resources/resources';
import { cardService } from '../../../../../services/card';
import {
  OptionsForm,
  buildInitialForm,
  getPriceChangingOptions,
  processForm,
} from '../../../../Common/Options';
import { ProductSelector } from '../ProductSelector';
import { cardFormFactorOptionId } from '../constants';
import { GenericCardData, TopUpContextType } from '../types';
import { getCardFormFactorProductOptionValueId } from '../utils';

interface TopUpGenericCardProps {
  contextType: TopUpContextType;
  cardType: CardType;
  cardFormFactor?: CardFormFactor;
  genericCardData: GenericCardData;
  setGenericCardData: (newData: GenericCardData) => void;
  onSaveClick: (
    productId: number,
    options: Dictionary<any>,
    serializedOptions: string,
    price: ComputedCardProductPriceModel,
  ) => any;
  errors: ErrorState;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
  clearErrors: (optionId: number | string) => void;
  form?: Dictionary<any>;
}

export const TopUpGenericCard = (props: TopUpGenericCardProps) => {
  const {
    contextType,
    cardType,
    cardFormFactor,
    genericCardData,
    setGenericCardData,
    onSaveClick,
    errors,
    setErrors,
    clearErrors,
  } = props;
  const {
    cardSeries,
    thumbnail,
    isSaveButtonLoading,
    isPriceLoading,
    products,
    selectedProduct,
    areTopUpDetailsLoading,
    topUpDetails,
  } = genericCardData;

  const { t: translate } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<Dictionary<any>>({});
  const [price, setPrice] = useState<ComputedCardProductPriceModel>({
    price: 0,
    netPrice: 0,
    budgetTax: 0,
    employeeTax: 0,
  });
  let initialForm = {};
  if (props.form) {
    initialForm = props.form;
  } else if (topUpDetails) {
    initialForm = buildInitialForm(
      topUpDetails.options,
      topUpDetails.recurrences,
      translate,
      [] as DependentModel[],
    );
  }
  const [form, setForm] = useState<Dictionary<any>>(initialForm);

  useEffect(() => {
    const newSelectedOptions = getPriceChangingOptions(
      form,
      topUpDetails?.options ?? [],
    );
    if (_.isEqual(selectedOptions, newSelectedOptions)) {
      return;
    }

    setSelectedOptions(newSelectedOptions);

    const calculatePrice = async () => {
      setGenericCardData(
        produce(genericCardData, d => {
          d.isPriceLoading = true;
        }),
      );
      const result = await cardService.calculatePrice(
        selectedProduct!.productId,
        newSelectedOptions,
      );
      setGenericCardData(
        produce(genericCardData, d => {
          d.isPriceLoading = false;
        }),
      );
      if (!result.hasErrors) {
        setPrice({
          price: result.data.price,
          netPrice: result.data.netPrice,
          budgetTax: result.data.budgetTax,
          employeeTax: result.data.employeeTax,
        });
      }
    };
    calculatePrice().catch();
  }, [
    form,
    genericCardData,
    props,
    selectedOptions,
    selectedProduct,
    setGenericCardData,
    topUpDetails?.options,
  ]);

  const onProductChange = async (value: CardProductModel) => {
    // only to be displayed for the ops team, not actually used in code
    updateQueryStringParameter('productId', value.productId.toString());
    setGenericCardData(
      produce(genericCardData, d => {
        d.selectedProduct = value;
        d.areTopUpDetailsLoading = true;
      }),
    );
    const resp = await cardService.getTopUpCardDetailsByProduct(
      value.productId,
    );
    if (!resp.hasErrors) {
      const topUpDetails = resp.data.topUpDetails!;
      setGenericCardData(
        produce(genericCardData, d => {
          d.selectedProduct = value;
          d.topUpDetails = resp.data.topUpDetails;
          d.areTopUpDetailsLoading = false;
        }),
      );
      setForm(
        buildInitialForm(
          topUpDetails.options,
          topUpDetails.recurrences,
          translate,
          [] as DependentModel[],
        ),
      );
    }
  };

  const onSaveButtonClick = () => {
    const formWithCardFormFactor = cardFormFactor
      ? {
          ...form,
          [cardFormFactorOptionId]: getCardFormFactorProductOptionValueId(
            topUpDetails!.options,
            cardFormFactor,
          ),
        }
      : form;
    onSaveClick(
      selectedProduct!.productId,
      formWithCardFormFactor,
      processForm(formWithCardFormFactor, topUpDetails!.options),
      price,
    );
  };

  return (
    <div className="topup-area">
      <div className="topup-procedure">
        {/* Selected card section */}
        <div className="topup-section pt pb">
          <div className="card-details-small">
            <div className="cds-image">
              <div>
                <img
                  src={rewriteImageUrl(thumbnail)}
                  alt={translate(
                    _.get(resources, cardTypeDescriptionMapping[cardType]),
                  )}
                />
              </div>
            </div>
            <div className="cds-details">
              <div className="cds-name">
                {translate(
                  _.get(resources, cardTypeDescriptionMapping[cardType]),
                )}
              </div>
              {cardSeries && (
                <div className="cds-id">
                  {translate(resources.employeeCards.cardSeries)}: {cardSeries}
                </div>
              )}

              <div className="cds-availability">
                <span className="av-nm">
                  {translate(resources.employeeCards.topUp.availableNextMonth)}
                </span>
              </div>
              <div className="cds-availability-info">
                {translate(resources.employeeCards.topUp.schedule)}
              </div>
            </div>
          </div>
        </div>
        <div className="topup-section-separator"></div>

        {/* Product selector */}
        <ProductSelector
          products={products}
          selectedProduct={selectedProduct}
          onProductSelect={onProductChange}
          usePriceSelector={cardType === CardType.Meal}
          areTopUpDetailsLoading={areTopUpDetailsLoading}
        />

        <Loader loading={!!areTopUpDetailsLoading} loader={<LoadingSpinner />}>
          {!!topUpDetails && (
            <>
              <div className="topup-section pt-20 pb">
                {(!!topUpDetails.options.length ||
                  !!topUpDetails.recurrences.length) && (
                  <div className="products-options-container">
                    {!!topUpDetails.recurrences.length && (
                      <Dropdown
                        data={topUpDetails.recurrences}
                        dataItemKey="id"
                        textField="title"
                        value={form['recurrenceId']}
                        label={`*${translate(
                          resources.products.recurrencePeriod,
                        )}`}
                        onChange={e => {
                          setForm(
                            produce(draft => {
                              draft['recurrenceId'] = e.value;
                            }),
                          );
                        }}
                        errors={errors.recurrenceId}
                        clearErrors={() => clearErrors('recurrenceId')}
                        disabled={topUpDetails.recurrences.length == 1}
                      />
                    )}
                    <OptionsForm
                      options={topUpDetails.options}
                      form={form}
                      setForm={setForm}
                      errors={errors}
                      clearErrors={clearErrors}
                      setErrors={setErrors}
                      productId={selectedProduct!.productId}
                    />
                  </div>
                )}
              </div>

              {/* Summary and action section */}
              <div className="topup-section-separator"></div>

              <div className="topup-section pt">
                <div className="topup-summary">
                  <div>
                    <span className="topup-summary-label">
                      {translate(resources.employeeCards.topUp.netPrice)}
                    </span>
                    <span className="topup-summary-value">
                      <Loader loading={!!isPriceLoading} loader={<Skeleton />}>
                        {formatPrice(price.netPrice)} {config.currency}
                      </Loader>
                    </span>
                  </div>
                  <div>
                    <span className="topup-summary-label topup-summary-taxes">
                      {translate(resources.employeeCards.topUp.budgetTaxes)}
                      <PopoverDialog
                        content={
                          <div>
                            {price.budgetTax && price.budgetTax !== 0
                              ? translate(
                                  resources.employeeCards.topUp.budgetTaxesInfo,
                                )
                              : translate(
                                  resources.employeeCards.topUp
                                    .noBudgetTaxesInfo,
                                )}
                          </div>
                        }
                        appendTo={document.body}
                        type="hover"
                        placement="top"
                        closeFromContent
                        className="topup-tooltip"
                      >
                        <div>
                          <Icons.InfoMedium />
                        </div>
                      </PopoverDialog>
                    </span>
                    <span className="topup-summary-value">
                      <Loader loading={!!isPriceLoading} loader={<Skeleton />}>
                        {formatPrice(price.budgetTax)} {config.currency}
                      </Loader>
                    </span>
                  </div>
                </div>

                <div className="topup-summary-separator">
                  <Icons.SolidSeparator />
                </div>

                <div className="topup-summary topup-summary-total-row">
                  <div>
                    <span className="topup-summary-total-label">
                      {translate(resources.employeeCards.topUp.total)}
                    </span>
                    <span className="topup-summary-total-value">
                      <Loader loading={!!isPriceLoading} loader={<Skeleton />}>
                        {formatPrice(price.price)} {config.currency}
                      </Loader>
                    </span>
                  </div>
                  <div>
                    <span className="topup-summary-sub-label">
                      {translate(resources.employeeCards.topUp.benefitBudget)}
                    </span>
                  </div>
                </div>

                <div className="topup-summary-separator">
                  <Icons.DashedSeparator />
                </div>

                <div className="topup-summary">
                  <div>
                    <span className="topup-summary-label topup-summary-taxes">
                      {translate(resources.employeeCards.topUp.salaryTaxes)}
                      <PopoverDialog
                        content={
                          <div>
                            {price.employeeTax && price.employeeTax !== 0
                              ? translate(
                                  resources.employeeCards.topUp.salaryTaxesInfo,
                                )
                              : translate(
                                  resources.employeeCards.topUp
                                    .noSalaryTaxesInfo,
                                )}
                          </div>
                        }
                        appendTo={document.body}
                        type="hover"
                        placement="top"
                        closeFromContent
                        className="topup-tooltip"
                      >
                        <div>
                          <Icons.InfoMedium />
                        </div>
                      </PopoverDialog>
                    </span>
                    <span className="topup-summary-value">
                      <Loader loading={!!isPriceLoading} loader={<Skeleton />}>
                        {formatPrice(price.employeeTax)} {config.currency}
                      </Loader>
                    </span>
                  </div>
                </div>
                <div className="topup-actions">
                  <Button
                    className="btn btn-solid btn-lg btn-full"
                    onClick={onSaveButtonClick}
                    loading={isSaveButtonLoading}
                    disabled={isPriceLoading}
                  >
                    {contextType === TopUpContextType.Buy
                      ? translate(resources.continue)
                      : translate(resources.employeeCards.topUp.transfer)}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Loader>
      </div>
      <div
        className="topup-info"
        dangerouslySetInnerHTML={{
          __html: !areTopUpDetailsLoading
            ? topUpDetails?.description ?? ''
            : '',
        }}
      />
    </div>
  );
};
